<template>
  <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
    <div class="card-header bg-secondary text-white">
      <h5 class="mb-0"><i class="fa fa-ban me-2"></i>Odoo Refused This Order's Invoice Request</h5>
    </div>
    <div class="card-body">
      <div class="alert alert-danger outline mb-3">
        <i class="fa fa-exclamation-triangle me-2"></i>
        <strong class="d-block mb-1">Nothing Was Queued In Odoo</strong>
        <span class="oa-rejected__lead">
          Odoo received an invoice request for this order and refused it, so no invoice entry was
          created.
        </span>
      </div>

      <div v-if="recovery.validationErrors?.length" class="oa-rejected__block">
        <div class="oa-rejected__label">Reason Reported By Odoo</div>
        <ul class="oa-rejected__errors">
          <li v-for="(reason, i) in recovery.validationErrors" :key="i">{{ reason }}</li>
        </ul>
      </div>
      <div v-else-if="recovery.errorMessage" class="oa-rejected__block">
        <div class="oa-rejected__label">Reason Reported By Odoo</div>
        <pre class="oa-rejected__text">{{ recovery.errorMessage }}</pre>
      </div>

      <div class="oa-rejected__block">
        <div class="oa-rejected__label">Why It Cannot Be Fixed Here</div>
        <div class="oa-rejected__lead">{{ unrecoverableExplanation }}</div>
      </div>

      <dl class="oa-rejected__meta">
        <div v-if="recovery.attemptedAt">
          <dt>Last Attempt</dt>
          <dd>
            <ZonedDateTime
              :value="parsedAttemptedAt"
              :zone="userTimezone"
              kind="user"
              layout="inline"
            />
          </dd>
        </div>
        <div v-if="recovery.httpCode">
          <dt>Odoo Replied</dt>
          <dd>HTTP {{ recovery.httpCode }}</dd>
        </div>
        <div v-if="recovery.sourceLogId != null">
          <dt>Odoo Log</dt>
          <dd>#{{ recovery.sourceLogId }}</dd>
        </div>
      </dl>

      <div class="d-flex gap-2 flex-wrap mt-3">
        <a
          v-if="findDataLink !== '#'"
          :href="findDataLink"
          target="_blank"
          rel="noopener"
          class="btn btn-outline-primary btn-sm"
        >
          <i class="fa fa-external-link me-1"></i>Order Details
        </a>
        <a
          v-if="findDataLink !== '#'"
          :href="findDataLink"
          target="_blank"
          rel="noopener"
          class="btn btn-outline-primary btn-sm"
        >
          <i class="fa fa-external-link me-1"></i>Open Full Diagnostics
        </a>
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="$emit('restart')">
          <i class="fa fa-refresh me-1"></i>Look Up Another Order
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { parseOdooDateTime } from '@/services/ryd-pay-operations/order-amendments/odoo-payment-recovery';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';

const props = defineProps({
  recovery: { type: Object, required: true },
  uuid: { type: String, default: '' },
});

defineEmits(['restart']);
const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);

const EXPLANATIONS = {
  'origin-mismatch':
    'The stored request belongs to a different order, so its data cannot be reused for this one.',
  'malformed-json':
    'The stored request could not be read back as valid data, so there is nothing to correct and resend.',
  'no-request-body':
    'Odoo logged the call but kept no copy of what was sent, so there is nothing to correct and resend.',
};

const unrecoverableExplanation = computed(
  () =>
    EXPLANATIONS[props.recovery.unrecoverableReason] ??
    'The stored request cannot be reused for this order.',
);

const findDataLink = computed(() =>
  props.uuid
    ? `/ryd-customer-support/find-data-by-order-uuid?uuid=${encodeURIComponent(props.uuid)}`
    : '#',
);

// Odoo reports UTC without a zone marker, which reads as local time if shown raw.
const parsedAttemptedAt = computed(
  () => parseOdooDateTime(props.recovery.attemptedAt) || props.recovery.attemptedAt,
);

function openFindData() {
  if (findDataLink.value === '#') return;
  window.open(findDataLink.value, '_blank', 'noopener');
}
</script>

<style scoped>
.oa-rejected__lead {
  color: var(--bs-body-color, #212529);
  font-size: 0.9rem;
  text-transform: none;
}

.oa-rejected__block + .oa-rejected__block {
  margin-top: 0.9rem;
}

.oa-rejected__label {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.7rem;
  font-weight: 800;
  color: var(--bs-secondary-color, #6c757d);
  margin-bottom: 0.35rem;
}

.oa-rejected__errors {
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.88rem;
  color: var(--bs-body-color, #212529);
}

.oa-rejected__errors li + li {
  margin-top: 0.25rem;
}

.oa-rejected__text {
  margin: 0;
  font-size: 0.85rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--bs-body-color, #212529);
  font-family: 'Courier New', Courier, monospace;
}

.oa-rejected__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.5rem;
  margin: 1rem 0 0 0;
  font-size: 0.82rem;
}

.oa-rejected__meta > div {
  display: flex;
  gap: 0.4rem;
}

.oa-rejected__meta dt {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--bs-secondary-color, #6c757d);
  align-self: center;
}

.oa-rejected__meta dd {
  margin: 0;
  color: var(--bs-body-color, #212529);
  font-weight: 500;
}
</style>
